// Loan Compliance Module v1.0 PR 7 + PR 9 — DTOs for the Track 3 Annual
// Legal Compliance Review. Mirrors the C# records in
// PhoenixCapital.Core/DTOs/Compliance/LoanLegalReviewDtos.cs (loan scope,
// modules 3.1–3.9) and PoolLegalReviewDtos.cs (pool scope, modules
// 3.10–3.15). The shared workspace consumes both via a normalized
// projection (see LegalReviewBindings).

import type { ComplianceSeverity } from "./types";

export type LegalReviewScope = "loan" | "pool";

export type LoanLegalReviewStatus =
  | "Scoping"
  | "Fieldwork"
  | "Draft"
  | "InReview"
  | "Finalized"
  | "Reissued";

export type LegalReviewConclusion =
  | "Compliant"
  | "ExceptionRaised"
  | "NotApplicable"
  | "Pending";

// ============================== Loan-scope DTOs ==========================

export interface LoanLegalReviewModuleDto {
  id: string;
  loanLegalReviewId: string;
  moduleCode: string;
  moduleName: string;
  workingNotes: string | null;
  conclusion: LegalReviewConclusion;
  conclusionNote: string | null;
  completedByUserId: string | null;
  completedAt: string | null;
  relatedComplianceExceptionId: string | null;
}

export interface LoanLegalReviewDto {
  id: string;
  loanId: string;
  auditYear: number;
  attorneyUserId: string;
  supervisorUserId: string | null;
  status: LoanLegalReviewStatus;
  scopingMemo: string | null;
  finalLlcrReportSubmissionId: string | null;
  certificationRecordId: string | null;
  signedByUserId: string | null;
  signedAt: string | null;
  reissueOfId: string | null;
  privilegeProtected: boolean;
  modules: LoanLegalReviewModuleDto[];
  createdAt: string;
  createdByUserId: string | null;
  versionId: number;
}

export interface LoanLegalReviewSummaryDto {
  id: string;
  loanId: string;
  auditYear: number;
  status: LoanLegalReviewStatus;
  moduleCount: number;
  compliantCount: number;
  exceptionRaisedCount: number;
  notApplicableCount: number;
  pendingCount: number;
  signedAt: string | null;
  finalLlcrReportSubmissionId: string | null;
}

export interface LegalReviewMemoDto {
  id: string;
  loanId: string;
  attorneyUserId: string;
  subject: string;
  body: string;
  relatedEventType: string | null;
  relatedEventId: string | null;
  signedAt: string | null;
  certificationRecordId: string | null;
  createdAt: string;
}

// ============================== Pool-scope DTOs ==========================

export interface PoolLegalReviewModuleDto {
  id: string;
  poolLegalReviewId: string;
  moduleCode: string;
  moduleName: string;
  workingNotes: string | null;
  conclusion: LegalReviewConclusion;
  conclusionNote: string | null;
  completedByUserId: string | null;
  completedAt: string | null;
  relatedComplianceExceptionId: string | null;
}

export interface PoolLegalReviewDto {
  id: string;
  spvPoolId: string;
  auditYear: number;
  attorneyUserId: string;
  supervisorUserId: string | null;
  status: LoanLegalReviewStatus;
  scopingMemo: string | null;
  finalPlcrReportSubmissionId: string | null;
  officerCertSupportReportSubmissionId: string | null;
  certificationRecordId: string | null;
  signedByUserId: string | null;
  signedAt: string | null;
  reissueOfId: string | null;
  privilegeProtected: boolean;
  modules: PoolLegalReviewModuleDto[];
  createdAt: string;
  createdByUserId: string | null;
  versionId: number;
}

export interface PoolLegalReviewSummaryDto {
  id: string;
  spvPoolId: string;
  auditYear: number;
  status: LoanLegalReviewStatus;
  moduleCount: number;
  compliantCount: number;
  exceptionRaisedCount: number;
  notApplicableCount: number;
  pendingCount: number;
  signedAt: string | null;
  finalPlcrReportSubmissionId: string | null;
  officerCertSupportReportSubmissionId: string | null;
}

export interface PoolLegalReviewMemoDto {
  id: string;
  spvPoolId: string;
  attorneyUserId: string;
  subject: string;
  body: string;
  relatedEventType: string | null;
  relatedEventId: string | null;
  signedAt: string | null;
  certificationRecordId: string | null;
  createdAt: string;
}

// ============================== Request DTOs =============================

export interface ReviewedDocumentInput {
  documentId: string;
  documentVersion: string;
}

export interface CreateLoanLegalReviewRequest {
  auditYear: number;
  attorneyUserId: string;
}

export interface CreatePoolLegalReviewRequest {
  auditYear: number;
  attorneyUserId: string;
}

export interface UpdateScopingMemoRequest {
  scopingMemo: string;
}

export interface UpdatePoolScopingMemoRequest {
  scopingMemo: string;
}

export interface RecordModuleConclusionRequest {
  moduleCode: string;
  conclusion: LegalReviewConclusion;
  conclusionNote?: string | null;
  workingNotes?: string | null;
  reviewedDocuments?: ReviewedDocumentInput[];
  exceptionSeverity?: ComplianceSeverity | null;
  exceptionTitle?: string | null;
  exceptionDescription?: string | null;
  programDocumentCitation?: string | null;
}

export type RecordPoolModuleConclusionRequest = RecordModuleConclusionRequest;

export interface SupervisorAcceptanceRequest {
  supervisorUserId: string;
}

export interface PoolSupervisorAcceptanceRequest {
  supervisorUserId: string;
}

export interface ReissueLoanLegalReviewRequest {
  reason: string;
}

export interface ReissuePoolLegalReviewRequest {
  reason: string;
}

export interface CreateLegalReviewMemoRequest {
  subject: string;
  body: string;
  relatedEventType?: string | null;
  relatedEventId?: string | null;
}

export type CreatePoolLegalReviewMemoRequest = CreateLegalReviewMemoRequest;

export interface SignLegalReviewMemoRequest {
  certificationDefinitionId: string;
}

export interface SignPoolLegalReviewMemoRequest {
  certificationDefinitionId: string;
}

export interface FinalizeLoanLegalReviewRequest {
  certificationDefinitionId: string;
}

export interface FinalizePoolLegalReviewRequest {
  certificationDefinitionId: string;
}

// ============================== Discriminators ===========================

/** The summary projection that non-A3/E4 callers receive from the list and
 * get-by-id endpoints (loan scope). The discriminator we use is the
 * presence of the <c>modules</c> array (full DTO) versus the aggregate
 * counts (summary). */
export function isFullReview(
  v: LoanLegalReviewDto | LoanLegalReviewSummaryDto,
): v is LoanLegalReviewDto {
  return Array.isArray((v as LoanLegalReviewDto).modules);
}

/** Pool-scope mirror of {@link isFullReview}. */
export function isFullPoolReview(
  v: PoolLegalReviewDto | PoolLegalReviewSummaryDto,
): v is PoolLegalReviewDto {
  return Array.isArray((v as PoolLegalReviewDto).modules);
}

// ============================== Constants ================================

/** Well-known seeded CertificationDefinition for the LLCR attorney sign-off
 * (Track 3 loan-level). Stays in lockstep with the
 * 20330401000000_Compliance_v1_PR6_Track3LegalReviewLoan migration. */
export const LLCR_CERTIFICATION_DEFINITION_ID =
  "bd3e11a6-9c46-4f5b-87b2-9f8e5c7d3a11";

/** Well-known seeded CertificationDefinition for the PLCR attorney sign-off
 * (Track 3 pool-level). Stays in lockstep with the
 * 20330501000000_Compliance_v1_PR8_Track3LegalReviewPool migration. */
export const PLCR_CERTIFICATION_DEFINITION_ID =
  "c7e94a22-1f38-4d5c-8eb6-2a4d9f1c5b12";

/** The nine loan-level Track 3 modules (3.1–3.9). Kept in sync with
 * <c>PhoenixCapital.Core.Enums.Compliance.LoanLegalReviewModuleCodes</c>. */
export const LOAN_LEGAL_REVIEW_MODULES: ReadonlyArray<{
  code: string;
  name: string;
}> = [
  { code: "3.1", name: "Entity / Org / Good-Standing" },
  { code: "3.2", name: "Eligible-Asset Re-Attestation" },
  { code: "3.3", name: "UCC Perfection (legal)" },
  { code: "3.4", name: "Personal Guaranty" },
  { code: "3.5", name: "VMP / RESPA / Anti-Kickback" },
  { code: "3.6", name: "Active Mgmt / AAMA" },
  { code: "3.7", name: "Tax / Licensing / Regulatory" },
  { code: "3.8", name: "Segment / Repurchase / Right-Size" },
  { code: "3.9", name: "Material Event Legal Disposition" },
];

/** The six pool-level Track 3 modules (3.10–3.15). Kept in sync with
 * <c>PhoenixCapital.Core.Enums.Compliance.PoolLegalReviewModuleCodes</c>. */
export const POOL_LEGAL_REVIEW_MODULES: ReadonlyArray<{
  code: string;
  name: string;
}> = [
  { code: "3.10", name: "Trust Separateness & Non-Consolidation" },
  { code: "3.11", name: "Dodd-Frank §15G Risk Retention" },
  { code: "3.12", name: "Reg D 506(b) Securities" },
  { code: "3.13", name: "Institutional Service-Provider Contracts" },
  { code: "3.14", name: "APA Compliance" },
  { code: "3.15", name: "Indenture Compliance & Officer's Certificate Support" },
];
